import { useSyncExternalStore } from 'react';
import { EventBus, EVENTS } from '../../../shared/EventBus';
import { ServiceRegistry } from '../../core/ServiceRegistry';
import type { CraftingRecipe } from '../../../shared/CharacterTypes';

export interface CraftResult {
  success: boolean;
  message: string;
  recipeId?: string;
  status?: 'confirmed' | 'refused' | 'unavailable' | 'uncertain';
}
export interface CraftingRoom {
  sessionId?: string;
  send(type: string, data: unknown): void;
  onMessage(type: string, handler: (data: any) => void): (() => void) | void;
}
interface Scope { room: CraftingRoom; sessionId: string; epoch: number }
interface Loan extends Scope { recipeId: string; outputItemId: string }
interface Snapshot { pendingRecipeId: string | null; lastResult: CraftResult | null }
const EMPTY: Snapshot = { pendingRecipeId: null, lastResult: null };
let snapshot = EMPTY;
let scope: Scope | null = null;
let loan: Loan | null = null;
let epoch = 0;
let blockedRoom: CraftingRoom | null = null;
let connectedKey: string | null = null;
let disposeReply: (() => void) | null = null;
let networkDisposers: Array<() => void> = [];
const subscribers = new Set<() => void>();

export function readCraftRoom(): CraftingRoom | null {
  const direct = (globalThis as any).nm?.getRoom?.();
  if (direct) return direct;
  try { return ServiceRegistry.getInstance().get('NetworkManager', { optional: true })?.getRoom?.() ?? null; }
  catch { return null; }
}
function publish(next: Snapshot): void {
  snapshot = next;
  for (const subscriber of subscribers) subscriber();
}
function stopReply(): void {
  const dispose = disposeReply;
  disposeReply = null;
  dispose?.();
}
function releaseIdleNetwork(): void {
  if (subscribers.size || loan) return;
  for (const dispose of networkDisposers) dispose();
  networkDisposers = [];
}
function retire(message: string): void {
  const previous = loan;
  loan = null;
  stopReply();
  epoch++;
  if (scope) scope = { ...scope, epoch };
  publish({ pendingRecipeId: null, lastResult: previous
    ? { success: false, status: 'uncertain', recipeId: previous.recipeId, message }
    : snapshot.lastResult?.status === 'uncertain' ? snapshot.lastResult : null });
}

/** Actual transport/session changes retire loans; closing a view does not. */
export function synchronizeCraftOwner(): Scope | null {
  const room = readCraftRoom();
  if (room && room === blockedRoom) return null;
  const sessionId = room?.sessionId ?? '';
  if (scope?.room === room && scope?.sessionId === sessionId) return scope;
  const hadLoan = Boolean(loan);
  retire('Connection changed. Check your bag before trying again.');
  scope = room ? { room, sessionId, epoch } : null;
  if (!hadLoan) publish(EMPTY);
  return scope;
}

function ensureNetwork(): void {
  if (networkDisposers.length) return;
  const bus = EventBus.getInstance();
  networkDisposers = [
    bus.on(EVENTS.NET_CONNECTED, data => {
      const key = JSON.stringify([data.roomId, data.roomInstanceId, data.sessionId, data.clientInstanceId]);
      if ((connectedKey !== null && connectedKey !== key) || (connectedKey === null && loan)) {
        retire('Connection changed. Check your bag before trying again.');
      }
      connectedKey = key;
      blockedRoom = null;
      synchronizeCraftOwner();
      releaseIdleNetwork();
    }),
    bus.on(EVENTS.NET_DISCONNECTED, () => {
      blockedRoom = scope?.room ?? null;
      retire('Connection lost. Check your bag after reconnecting.');
      releaseIdleNetwork();
    }),
  ];
}

function currentSnapshot(): Snapshot {
  const room = readCraftRoom();
  if (!room && snapshot.lastResult?.status === 'uncertain') return snapshot;
  return scope?.room === room && scope?.sessionId === (room?.sessionId ?? '') ? snapshot : EMPTY;
}
function subscribe(listener: () => void): () => void {
  subscribers.add(listener);
  ensureNetwork();
  synchronizeCraftOwner();
  return () => { subscribers.delete(listener); releaseIdleNetwork(); };
}
export function useCraftLoanSnapshot(): Snapshot {
  return useSyncExternalStore(subscribe, currentSnapshot, () => EMPTY);
}
export function reportCraftAvailability(result: CraftResult): void {
  if (!loan) publish({ pendingRecipeId: null, lastResult: result });
}

/** A single retained receiver is installed BEFORE send and survives every view close. */
export function startCraftLoan(recipe: CraftingRecipe): boolean {
  ensureNetwork();
  const owner = synchronizeCraftOwner();
  if (!owner || loan || !owner.sessionId) return false;
  const pending: Loan = { ...owner, recipeId: recipe.id, outputItemId: recipe.outputItemId };
  loan = pending;
  const receiver = (message: { success: boolean; reason?: string; itemId?: string; itemName?: string; quantity?: number }) => {
    if (loan !== pending || scope?.epoch !== pending.epoch || scope.room !== pending.room
      || readCraftRoom() !== pending.room || (pending.room.sessionId ?? '') !== pending.sessionId || blockedRoom === pending.room) return;
    if (message.success && message.itemId && message.itemId !== pending.outputItemId) return;
    // Failure has no recipe/request ID; one outstanding owned loan is the wire's available bound.
    loan = null;
    stopReply();
    const text = message.success ? message.itemName && typeof message.quantity === 'number'
      ? `Crafted ${message.quantity}x ${message.itemName}!` : 'Craft confirmed. Check your bag for the result.'
      : message.reason === 'inventory_full' ? 'Your bag is full.' : message.reason ?? 'Craft failed';
    publish({ pendingRecipeId: null, lastResult: { success: message.success,
      status: message.success ? 'confirmed' : 'refused', recipeId: pending.recipeId, message: text } });
    releaseIdleNetwork();
  };
  try {
    const dispose = owner.room.onMessage('craftResult', receiver);
    disposeReply = typeof dispose === 'function' ? dispose : null;
    publish({ pendingRecipeId: recipe.id, lastResult: null });
    owner.room.send('craftItem', { recipeId: recipe.id });
    return true;
  } catch {
    if (loan === pending) {
      loan = null;
      stopReply();
      publish({ pendingRecipeId: null, lastResult: { success: false, status: 'unavailable', recipeId: recipe.id,
        message: 'Craft request could not be sent. Check your connection.' } });
      releaseIdleNetwork();
    }
    return false;
  }
}

export function resetCraftLoansForTests(): void {
  loan = null;
  stopReply();
  for (const dispose of networkDisposers) dispose();
  networkDisposers = [];
  scope = null; blockedRoom = null; connectedKey = null; epoch++;
  publish(EMPTY);
}
